import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { isFormValid } from '../core/form-helpers';
import { Subject } from 'rxjs';
import { ModalComponent } from '../core/modal/modal.component';
import { Customer, Device, Lookup, PagedResults } from '../app.types';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import { MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponentDialog, createDefaultData } from '../confirm-dialog/confirm-dialog.component';
import { LookupService } from '../services/lookup/lookup.service';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';

@Component({
    selector: 'device-form-modal',
    templateUrl: './device-form-modal.component.html',
    styleUrls: ['./device-form-modal.component.scss'],
    standalone: false
})
export class DeviceFormModalComponent implements OnInit, OnDestroy {
  @Input() deviceId: string;
  @Input() triggerRefresh: Subject<null>;
  @Input() customers: Customer[];
  @Output() close: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild('customerSelect') customerSelect: MatSelect;

  assetTypes: Lookup[] = [];
  newAssetTypeID: string = null;

  form: FormGroup;
  requestInProgress = false;

  addingAssetType = false;

  refreshAssetTypes$ = new Subject<string>();
  destroy$ = new Subject();

  /**
   * CTOR
   */
  constructor(
    private formBuilder: FormBuilder,
    private deviceManagementService: DeviceManagementService,
    public dialog: MatDialog,
    private lookupService: LookupService
  ) {
    this.form = formBuilder.group({
      serialNumber: ['', Validators.required],
      customerId: ['', Validators.required],
      fleetId: ['', Validators.required],
      assetTypeId: [''],
      assetGroupPath: [''],
      APIKey: ['', Validators.minLength(0)],
      enabled: [false],
      enableRecordingMapRequest: [false],
      enableCamerasSnapshotRequest: [false]
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  /**
   * On Init
   */
  ngOnInit() {
    if (this.deviceId != null)
      {this.deviceManagementService.getDevice(this.deviceId).subscribe((device: Device) => {
        this.form.setValue({
          serialNumber: device.serialNumber,
          customerId: device.customer == null ? null : device.customer.id,
          fleetId: device.fleetId,
          assetTypeId: device.assetType?.id == null ? null : device.assetType.id,
          APIKey: (device.apiKey == null || device.apiKey.length==0) ? '' : device.apiKey,
          enabled: device.enabled,
          enableRecordingMapRequest: device.enableRecordingMapRequest,
          enableCamerasSnapshotRequest: device.enableCamerasSnapshotRequest,
          assetGroupPath: (device.assetGroupPath == null || device.assetGroupPath.length==0) ? '' : device.assetGroupPath,
        });
      });}

    this.refreshAssetTypes$.pipe(
      startWith(''),
      switchMap(x => {
        const assetTypes$: Observable<PagedResults<Lookup>> = this.lookupService.getList('AssetType', {});
        return combineLatest([assetTypes$, of(x)])
      }),
      takeUntil(this.destroy$)
    ).subscribe(([data, newAssetTypeID]) => {
      this.assetTypes = data.results;
      if(newAssetTypeID) {
        this.form.controls['assetTypeId'].setValue(newAssetTypeID);
      }
      if(!this.assetTypes.some(x => x.id === this.form.controls['assetTypeId'].value)) {
        this.form.controls['assetTypeId'].setValue(null);
      }
    });
  }
  

  submitForm() {
    if (!isFormValid(this.form)) {return;}

    const formData = this.form.getRawValue();
    formData.name = this.customerSelect.triggerValue;
    this.requestInProgress = true;

    // ADD
    if (this.deviceId == null) {
      const addConfirmText = 'Are you sure you wish to add device?';

      const dialogRef1 = this.dialog.open(ConfirmDialogComponentDialog, {
        data: createDefaultData(addConfirmText),
      });

      dialogRef1.afterClosed().subscribe((result) => {
        console.log(`Dialog result: ${result}`);

        if (result == null) {return;}

        if (result == false) {console.log("don't add device ");}

        if (result == true) {
          console.log('add  device ');

          this.deviceManagementService.addDevice(formData).subscribe((res) => {
            if (res != null) {
              this.triggerRefresh.next(null);
              this.modal.closeModal();
            }
          });
        }
      });
    }

    // EDIT
    else {
      const editConfirmText = 'Are you sure you wish to save changes for  ' + this.form.get('fleetId').value + '?';

      const dialogRef2 = this.dialog.open(ConfirmDialogComponentDialog, {
        data: createDefaultData(editConfirmText),
      });

      dialogRef2.afterClosed().subscribe((result) => {
        console.log(`Dialog result: ${result}`);

        if (result == null) {return;}

        if (result == false) {console.log("don't save changes for " + this.form.get('fleetId').value);}

        if (result == true) {
          console.log('save changes for device ' + this.form.get('fleetId').value);

          this.deviceManagementService.editDevice({ ...formData, id: this.deviceId }).subscribe((res) => {
            if (res != null) {
              this.triggerRefresh.next(null);
              this.modal.closeModal();
            }
          });
        }
      });
    }
  }

  /**
   * Deletes a device
   */
  deleteDevice() {
    const cancelConfirmText =
      'Are you sure you wish to delete device ' +
      this.form.get('fleetId').value +
      '?' +
      ' All related data (Health, Location, VideoExports Jobs) will be deleted too!';

    const dialogRef = this.dialog.open(ConfirmDialogComponentDialog, {
      data: createDefaultData(cancelConfirmText),
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);

      if (result == null) {return;}

      if (result == false) {console.log("don't delete device export");}

      if (result == true) {
        console.log('delete device ' + this.form.get('fleetId').value);
        if (this.deviceId) {
          this.deviceManagementService.deleteDevice(this.deviceId).subscribe((res) => {
            if (res != null) {
              this.triggerRefresh.next(null);
              this.modal.closeModal();
            }
          });
        }
      }
    });
  }

  /**
   * Modal Cleanup
   */
  modalCleanup() {
    // if a request was triggered, emit true to trigger a refresh of device table
    this.close.emit(this.requestInProgress);
    this.requestInProgress = false;
  }

  openAssetTypeExpander(){
    this.addingAssetType = true;
  }

  assetTypeExpanderClosed(assetTypeId: string) {
    this.addingAssetType = false;

    this.refreshAssetTypes$.next(assetTypeId);
  }

  assetTypeDeleted() {
    this.refreshAssetTypes$.next(null);
  }
}
